'use client'

import { AppRoute, MenuActionId } from '@common/enums'

import { useProjectsStore } from '@modules/projects/stores/projects.store'
import { switchCurrentProject } from '@modules/projects/utils/switchCurrentProject'
import { selectCurrentWorkspace } from '@modules/workspaces/stores/workspaces.selector'
import { useWorkspacesStore } from '@modules/workspaces/stores/workspaces.store'
import { updateLastUsedProject } from '@modules/workspaces/stores/workspaces.thunks'
import { useLayoutStore } from '@stores/layout/layout.store'

import { getRouteForProjectAction } from '../utils/getRouteForProjectAction'
import { resolveProjectId } from '../utils/resolveProjectId'

import { useNavigateTo } from './useNavigateTo'

type UseProjectSelectParams = {
  isPortal: boolean
}

export function useProjectSelect({ isPortal }: UseProjectSelectParams) {
  const currentWorkspace = useWorkspacesStore(selectCurrentWorkspace)
  const { redirectToPortal, navigateOrRedirect } = useNavigateTo({ isPortal })

  const openProjectDialogOrRedirect = () => {
    if (isPortal) {
      useLayoutStore.getState().openCreateProjectDialog()
    } else {
      redirectToPortal(AppRoute.DASHBOARD)
    }
  }

  const handleProjectSelect = (itemId: string) => {
    if (itemId === MenuActionId.CREATE_PROJECT_DIALOG) {
      openProjectDialogOrRedirect()
      return
    }

    if (itemId === MenuActionId.INVITE_PEOPLE) {
      if (isPortal) {
        useLayoutStore.getState().openInviteProjectDialog()
        return
      }

      const inviteRoute = getRouteForProjectAction(itemId)
      if (inviteRoute) {
        navigateOrRedirect(inviteRoute)
      }
      return
    }

    if (itemId === MenuActionId.PROJECT_MEMBERS) {
      navigateOrRedirect(AppRoute.SETTINGS_PROJECT_MEMBERS)
      return
    }

    const route = getRouteForProjectAction(itemId)
    if (route) {
      navigateOrRedirect(route)
      return
    }

    const projectId = resolveProjectId(itemId)
    const project = useProjectsStore.getState().projects.find((p) => p.projectId === projectId)

    if (project) {
      switchCurrentProject(project)
      if (currentWorkspace?.workspaceId) {
        void updateLastUsedProject(currentWorkspace.workspaceId, project.projectId)
      }
    }
  }

  return { handleProjectSelect, openProjectDialogOrRedirect }
}
